import React, { useMemo } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useLanguage } from '../../../contexts/LanguageContext';
import useExternalItemDescription from './hooks/useExternalItemDescription';
import ExternalItemDescriptionForm from './components/ExternalItemDescriptionForm';
import GenericTableFilter from '../../../components/common/GenericTableFilter';

const ExternalItemDescriptionPage = () => {
    const { th } = useLanguage();
    const {
        items,
        totalRecords,
        isLoading,
        isCreating,
        isUpdating,
        dialogVisible,
        selectedItem,
        page,
        pageSize,
        handleNew,
        handleEdit,
        handleSave,
        handlePage,
        handleCloseDialog,
        handleApplyFilters,
        handleResetFilters
    } = useExternalItemDescription();

    const filterFields = useMemo(
    () => [
        {
            field: 'partCode',
            headerName: th('externalItemDescription.partCode') || 'Parça Kodu',
            type: 'text',
           filterPlaceholder: th('externalItemDescription.partCodePlaceholder') || 'Parça kodu giriniz'
        },
        {
            field: 'partName',
            headerName: th('externalItemDescription.partName') || 'Parça Adı',
            type: 'text',
            filterPlaceholder: th('externalItemDescription.partNamePlaceholder') || 'Parça adı giriniz'
        }
    ],
    [th]
);

    const statusBodyTemplate = (rowData) => {
        const statusMap = {
            1: { label: 'Aktif', severity: 'success' },
            0: { label: 'Pasif', severity: 'danger' },
            2: { label: 'Beklemede', severity: 'warning' }
        };
        const status = statusMap[rowData.isActive] || statusMap[1];
        return <span className={`p-tag p-tag-${status.severity}`}>{status.label}</span>;
    };

    const actionBodyTemplate = (rowData) => {
        return (
            <div className="flex gap-1">
                <i
                    onClick={() => handleEdit(rowData)}
                    className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700"
                    style={{ fontSize: '1.1rem' }}
                    title={th('common.edit') || 'Düzenle'}
                />
            </div>
        );
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">{th('externalItemDescription.pageTitle') || 'Harici Parça Tanımları'}</h1>
                <div className="flex gap-2">
                    <Button
                        label={th('externalItemDescription.newItem') || 'Yeni Kalem'}
                        icon="pi pi-plus"
                        onClick={handleNew}
                    />
                </div>
            </div>

            <div className="mb-2">
                <GenericTableFilter
                    filterFields={filterFields}
                    initialFilters={{}}
                    onApplyFilters={handleApplyFilters}
                    onResetFilters={handleResetFilters}
                    customFilterTypes={{}}
                    customFilterOptions={{}}
                    hideLabels={true}
                    textboxWidth="500px"
                />
            </div>

                <DataTable
                    value={items}
                    loading={isLoading}
                    lazy
                    paginator
                    rows={pageSize}
                    first={(page - 1) * pageSize}
                    totalRecords={totalRecords}
                    onPage={handlePage}
                    rowsPerPageOptions={[10, 20, 50]}
                    emptyMessage={th('common.noRecords') || 'Kayıt bulunamadı'}
                    responsiveLayout="scroll"
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                >
                    <Column
                        field="partCode"
                        header={th('externalItemDescription.partCode') || 'Parça Kodu'}
                        sortable
                        style={{ minWidth: '150px' }}
                    />
                    <Column
                        field="partName"
                        header={th('externalItemDescription.partName') || 'Parça Adı'}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="originalPartName"
                        header={th('externalItemDescription.originalPartName') || 'Orijinal Adı'}
                        sortable
                        style={{ minWidth: '200px' }}
                    />
                    <Column
                        field="meins"
                        header={th('externalItemDescription.meins') || 'Ölçü Birimi'}
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="salesPrice"
                        header={th('externalItemDescription.salesPrice') || 'Satış Fiyatı'}
                        body={(rowData) => rowData.salesPrice ? rowData.salesPrice.toFixed(2) : '-'}
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="currency"
                        header={th('externalItemDescription.currency') || 'Para Birimi'}
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        field="isActive"
                        header={th('externalItemDescription.isActive') || 'Durum'}
                        body={statusBodyTemplate}
                        style={{ minWidth: '120px' }}
                    />
                    <Column
                        header={th('common.actions') || 'İşlemler'}
                        body={actionBodyTemplate}
                        style={{ minWidth: '100px', textAlign: 'center' }}
                    />
                </DataTable>

                <ExternalItemDescriptionForm
                    visible={dialogVisible}
                    onHide={handleCloseDialog}
                    onSubmit={handleSave}
                    initialData={selectedItem}
                    isLoading={isCreating || isUpdating}
                />
        </div>
    );
};

export default ExternalItemDescriptionPage;

